import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import {
  Plus,
  Search,
  Package,
  Layers,
  CheckCircle,
  Clock,
  Archive,
  Filter,
  Snowflake,
  AlertTriangle,
} from 'lucide-react';
import { FreezerOutTimeTimer } from '@/components/Freezer';
import { Badge } from '@/components/ui/badge';
import type { PlotterCut, PlotterCutStatus } from '@/types/legacy';
import { PlotterCutForm } from '@/components/PlotterCuts/PlotterCutForm';
import { PlotterCutCard } from '@/components/PlotterCuts/PlotterCutCard';

export default function PlotterCutsPage() {
  const { userRole, user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('packages');
  const [cuts, setCuts] = useState<PlotterCut[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<PlotterCutStatus | 'all'>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editingCut, setEditingCut] = useState<PlotterCut | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const fetchCuts = async () => {
    try {
      let query = api
        .from('plotter_cuts')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setCuts(data || []);
    } catch (error: any) {
      console.error('[PlotterCuts] Error cargando paquetes:', error);
      toast({
        title: 'Error',
        description: `No se pudieron cargar los paquetes: ${error?.message || 'Error desconocido'}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'packages') {
      fetchCuts();
    }
  }, [statusFilter, activeTab]);

  // Auto-abrir el form cuando llega ?focus=ID en la URL
  useEffect(() => {
    if (focusId && cuts.length > 0) {
      const target = cuts.find((c) => c.id === focusId);
      if (target) {
        setEditingCut(target);
        setFormOpen(true);
        searchParams.delete('focus');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [focusId, cuts]);
  useDataRefresh(() => {
    // No refrescar mientras el formulario está abierto para evitar resetear el estado
    if (activeTab !== 'packages' || formOpen) return;
    fetchCuts();
  }, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const handleEdit = (cut: PlotterCut) => {
    setEditingCut(cut);
    setFormOpen(true);
  };

  const handleNew = () => {
    setEditingCut(null);
    setFormOpen(true);
  };

  const handleDelete = async (cut: PlotterCut) => {
    if (!confirm(`¿Eliminar el paquete ${cut.package_code}?`)) return;

    try {
      const { error } = await api
        .from('plotter_cuts')
        .update({ is_active: false })
        .eq('id', cut.id);

      if (error) throw error;

      toast({ title: 'Eliminado', description: 'Paquete eliminado correctamente' });
      fetchCuts();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el paquete',
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (cut: PlotterCut, newStatus: PlotterCutStatus) => {
    try {
      const updateData: any = { status: newStatus };

      if (newStatus === 'consumed') {
        updateData.consumed_at = new Date().toISOString();
        updateData.consumed_by = user?.id;
      }

      const { error } = await api
        .from('plotter_cuts')
        .update(updateData)
        .eq('id', cut.id);

      if (error) throw error;

      toast({
        title: 'Estado actualizado',
        description: `Paquete marcado como ${newStatus}`,
      });
      fetchCuts();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el estado',
        variant: 'destructive',
      });
    }
  };

  const filteredCuts = cuts.filter(cut =>
    cut.package_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cut.package_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cut.primary_material?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cut.batch_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cut.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: cuts.length,
    available: cuts.filter(c => c.status === 'available').length,
    reserved: cuts.filter(c => c.status === 'reserved').length,
    inUse: cuts.filter(c => c.status === 'in_use').length,
    consumed: cuts.filter(c => c.status === 'consumed').length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Corte Plotter
            </h1>
            <p className="text-muted-foreground mt-2">
              Paquetes de fibra de carbono para corte
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-1">
            <TabsTrigger value="packages" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Paquetes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="packages" className="mt-6 space-y-6">
            {canManage && (
              <div className="flex justify-end">
                <Button onClick={handleNew} className="bg-gradient-magma">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Paquete
                </Button>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total</p>
                    <p className="text-2xl font-bold">{stats.total}</p>
                  </div>
                  <Package className="w-8 h-8 text-primary/30" />
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Disponibles</p>
                    <p className="text-2xl font-bold text-green-500">{stats.available}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500/30" />
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Reservados</p>
                    <p className="text-2xl font-bold text-yellow-500">{stats.reserved}</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-500/30" />
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">En Uso</p>
                    <p className="text-2xl font-bold text-blue-500">{stats.inUse}</p>
                  </div>
                  <Layers className="w-8 h-8 text-blue-500/30" />
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Consumidos</p>
                    <p className="text-2xl font-bold text-gray-500">{stats.consumed}</p>
                  </div>
                  <Archive className="w-8 h-8 text-gray-500/30" />
                </div>
              </Card>
            </div>

            {/* Panel de materiales fuera del congelador */}
            {cuts.filter(c => c.removed_from_freezer_at && c.status !== 'consumed').length > 0 && (
              <Card className="p-4 border-blue-300 bg-blue-50/30 dark:bg-blue-950/10">
                <div className="flex items-center gap-2 mb-3">
                  <Snowflake className="w-5 h-5 text-blue-600" />
                  <span className="font-medium">Materiales fuera del congelador</span>
                  <Badge variant="secondary">
                    {cuts.filter(c => c.removed_from_freezer_at && c.status !== 'consumed').length}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {cuts
                    .filter(c => c.removed_from_freezer_at && c.status !== 'consumed')
                    .map(cut => (
                      <FreezerOutTimeTimer
                        key={cut.id}
                        removedAt={cut.removed_from_freezer_at!}
                        maxMinutes={cut.max_out_time_minutes || 120}
                        materialName={cut.package_name}
                        batchNumber={cut.batch_number}
                        onReturn={async () => {
                          await api
                            .from('plotter_cuts')
                            .update({
                              removed_from_freezer_at: null,
                              removed_from_freezer_by: null,
                              status: 'available',
                            })
                            .eq('id', cut.id);
                          fetchCuts();
                        }}
                      />
                    ))
                  }
                </div>
              </Card>
            )}

            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por código, nombre, material, lote o ubicación..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="available">Disponibles</SelectItem>
                  <SelectItem value="reserved">Reservados</SelectItem>
                  <SelectItem value="in_use">En Uso</SelectItem>
                  <SelectItem value="consumed">Consumidos</SelectItem>
                  <SelectItem value="damaged">Dañados</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : filteredCuts.length === 0 ? (
              <Card className="p-12 text-center">
                <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg text-muted-foreground">
                  {searchTerm || statusFilter !== 'all'
                    ? 'No se encontraron paquetes con esos filtros'
                    : 'No hay paquetes de corte plotter'}
                </p>
                {canManage && !searchTerm && statusFilter === 'all' && (
                  <Button variant="outline" className="mt-4" onClick={handleNew}>
                    Crear primer paquete
                  </Button>
                )}
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredCuts.map((cut) => (
                  <PlotterCutCard
                    key={cut.id}
                    cut={cut}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onStatusChange={handleStatusChange}
                    onRefresh={fetchCuts}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          
        </Tabs>
      </div>

      <PlotterCutForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={fetchCuts}
        editingCut={editingCut}
      />
    </DashboardLayout>
  );
}
